/**
 * @module api/requests/gnss
 */

import API_ENDPOINTS from "./apiEndpoints";

/**
 * Fetch all GNSS station details.
 * @memberof module:api/requests/gnss
 * @function
 * @name getAllStationsDetails
 * @param {string} accessToken - Access token for authorization.
 * @param {string} tenantId - Tenant ID.
 * @returns {Promise<any>} JSON response containing stations details.
 */
export const getAllStationsDetails = async (accessToken, tenantId) => {
  const response = await fetch(
    `${API_ENDPOINTS.gnssApi.baseUrl}${API_ENDPOINTS.gnssApi.endpoints.stations.getAll}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
        "X-Tenant-Id": tenantId,
      },
    }
  );

  return response;
};

/**
 * Add new station details.
 * @memberof module:api/requests/gnss
 * @function
 * @name addStationDetails
 * @param {Object} stationDetailsPayload - Payload containing station details.
 * @param {string} accessToken - Access token for authorization.
 * @param {string} tenantId - Tenant ID.
 * @returns {Promise<any>} JSON response from API.
 */
export const addStationDetails = async (
  groupName,
  stationId,
  stationDetailsPayload,
  accessToken,
  tenantId
) => {
  const response = await fetch(
    `${
      API_ENDPOINTS.gnssApi.baseUrl
    }${API_ENDPOINTS.gnssApi.endpoints.stations.add(groupName, stationId)}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
        "X-Tenant-Id": tenantId,
      },
      body: JSON.stringify({ details: stationDetailsPayload }),
    }
  );
  return response;
};

/**
 * Update existing station details.
 * @memberof module:api/requests/gnss
 * @function
 * @name updateStationDetails
 * @param {string} stationId - ID of the station to update.
 * @param {Object} stationDetailsPayload - Payload containing updated station details.
 * @param {string} accessToken - Access token for authorization.
 * @param {string} tenantId - Tenant ID.
 * @returns {Promise<any>} JSON response from API.
 */
export const updateStationDetails = async (
  groupName,
  stationId,
  stationDetailsPayload,
  accessToken,
  tenantId
) => {
  const response = await fetch(
    `${
      API_ENDPOINTS.gnssApi.baseUrl
    }${API_ENDPOINTS.gnssApi.endpoints.stations.update(groupName, stationId)}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
        "X-Tenant-Id": tenantId,
      },
      body: JSON.stringify({ details: stationDetailsPayload }),
    }
  );

  return response;
};

/**
 * Delete a station by its ID.
 * @memberof module:api/requests/gnss
 * @function
 * @name deleteStation
 * @param {string} stationId - ID of the station to delete.
 * @param {string} accessToken - Access token for authorization.
 * @param {string} tenantId - Tenant ID.
 * @returns {Promise<any>} JSON response from API.
 */
export const deleteStation = async (
  groupName,
  stationId,
  accessToken,
  tenantId
) => {
  const response = await fetch(
    `${
      API_ENDPOINTS.gnssApi.baseUrl
    }${API_ENDPOINTS.gnssApi.endpoints.stations.deleteById(
      groupName,
      stationId
    )}`,
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
        "X-Tenant-Id": tenantId,
      },
    }
  );
  return response;
};

/**
 * Get the latest periodic report for a station.
 * @memberof module:api/requests/gnss
 * @function
 * @name getLastReportByStation
 * @param {string} stationId - Station ID.
 * @param {string} accessToken - Access token for authorization.
 * @param {string} tenantId - Tenant ID.
 * @returns {Promise<any>} JSON response containing the latest report.
 */
export const getLastReportByStation = async (
  groupName,
  stationId,
  accessToken,
  tenantId
) => {
  const response = await fetch(
    `${
      API_ENDPOINTS.gnssApi.baseUrl
    }${API_ENDPOINTS.gnssApi.endpoints.periodic_reports.getLast(
      groupName,
      stationId
    )}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
        "X-Tenant-Id": tenantId,
      },
    }
  );

  return response;
};

/**
 * Get periodic report by station and date.
 * @memberof module:api/requests/gnss
 * @function
 * @name getReportByStationAndDate
 * @param {string} stationId - Station ID.
 * @param {string} date - Date string for report.
 * @param {string} accessToken - Access token for authorization.
 * @param {string} tenantId - Tenant ID.
 * @returns {Promise<any>} JSON response containing the report.
 */
export const getReportByStationAndDate = async (
  groupName,
  stationId,
  date,
  accessToken,
  tenantId
) => {
  const response = await fetch(
    `${
      API_ENDPOINTS.gnssApi.baseUrl
    }${API_ENDPOINTS.gnssApi.endpoints.periodic_reports.getByDate(
      groupName,
      stationId,
      date
    )}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
        "X-Tenant-Id": tenantId,
      },
    }
  );

  return response;
};

/**
 * Get all periodic report dates for a station.
 * @memberof module:api/requests/gnss
 * @function
 * @name getReportsDatesById
 * @param {string} stationId - Station ID.
 * @param {string} accessToken - Access token for authorization.
 * @param {string} tenantId - Tenant ID.
 * @returns {Promise<any>} JSON response containing report dates.
 */
export const getReportsDatesById = async (
  groupName,
  stationId,
  accessToken,
  tenantId
) => {
  const response = await fetch(
    `${
      API_ENDPOINTS.gnssApi.baseUrl
    }${API_ENDPOINTS.gnssApi.endpoints.periodic_reports.getAllDates(
      groupName,
      stationId
    )}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
        "X-Tenant-Id": tenantId,
      },
    }
  );

  return response;
};

/**
 * Delete a periodic report by station ID and date.
 * @memberof module:api/requests/gnss
 * @function
 * @name deleteReport
 * @param {string} stationId - Station ID.
 * @param {string} date - Date of the report to delete.
 * @param {string} accessToken - Access token for authorization.
 * @param {string} tenantId - Tenant ID.
 * @returns {Promise<any>} JSON response from API.
 */
export const deleteReport = async (
  groupName,
  stationId,
  date,
  accessToken,
  tenantId
) => {
  const response = await fetch(
    `${
      API_ENDPOINTS.gnssApi.baseUrl
    }${API_ENDPOINTS.gnssApi.endpoints.periodic_reports.deleteByDate(
      groupName,
      stationId,
      date
    )}`,
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
        "X-Tenant-Id": tenantId,
      },
    }
  );
  const data = await response.json();

  return data;
};

/**
 * Delete a periodic report by station ID and date.
 * @memberof module:api/requests/gnss
 * @function
 * @name deleteReport
 * @param {string} stationId - Station ID.
 * @param {string} date - Date of the report to delete.
 * @param {string} accessToken - Access token for authorization.
 * @param {string} tenantId - Tenant ID.
 * @returns {Promise<any>} JSON response from API.
 */
export const getSpectrogramImage = async (
  groupName,
  stationId,
  targetWidth,
  targetHeight,
  accessToken,
  tenantId
) => {

  const response = await fetch(
    `${
      API_ENDPOINTS.gnssApi.baseUrl
    }${API_ENDPOINTS.gnssApi.endpoints.realtime_reports.getSpectrogram(
      groupName,
      stationId,
      targetWidth,
      targetHeight
    )}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
        "X-Tenant-Id": tenantId,
      },
    }
  );
  return response;
};

/**
 * Get alerts by station identifier and group.
 * @memberof module:api/requests/gnss
 * @function
 * @name deleteReport
 * @param {string} stationId - Station ID.
 * @param {string} limit - Limit of latest alerts.
 * @param {string} accessToken - Access token for authorization.
 * @param {string} tenantId - Tenant ID.
 * @returns {Promise<any>} JSON response from API.
 */
export const getAlertsByStationId = async (
  groupName,
  stationId,
  limit,
  accessToken,
  tenantId
) => {

  const response = await fetch(
    `${
      API_ENDPOINTS.gnssApi.baseUrl
    }${API_ENDPOINTS.gnssApi.endpoints.realtime_reports.getAlerts(
      groupName,
      stationId,
      limit
    )}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
        "X-Tenant-Id": tenantId,
      },
    }
  );
  return response;
};

export const getAlertsByStationIdFiltered = async (
  groupName,
  stationId,
  filters,
  accessToken,
  tenantId
) => {

  const response = await fetch(
    `${
      API_ENDPOINTS.gnssApi.baseUrl
    }${API_ENDPOINTS.gnssApi.endpoints.realtime_reports.getAlertsFiltered(
      groupName,
      stationId,
      filters
    )}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
        "X-Tenant-Id": tenantId,
      },
    }
  );
  return response;
};

export const getAlertsIdsByStationId = async (
  groupName,
  stationId,
  date,
  accessToken,
  tenantId
) => {

  const response = await fetch(
    `${
      API_ENDPOINTS.gnssApi.baseUrl
    }${API_ENDPOINTS.gnssApi.endpoints.realtime_reports.getAlertsIds(
      groupName,
      stationId,
      date
    )}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
        "X-Tenant-Id": tenantId,
      },
    }
  );
  return response;
};

export const getAlertByStationId = async (
  groupName,
  stationId,
  alertId,
  accessToken,
  tenantId
) => {

  const response = await fetch(
    `${
      API_ENDPOINTS.gnssApi.baseUrl
    }${API_ENDPOINTS.gnssApi.endpoints.realtime_reports.getAlert(
      groupName,
      stationId,
      alertId
    )}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
        "X-Tenant-Id": tenantId,
      },
    }
  );
  return response;
};

export const getAlertAnalyticsByStationIdAndAlertId = async (
  groupName,
  stationId,
  alertId,
  accessToken,
  tenantId
) => {

  const response = await fetch(
    `${
      API_ENDPOINTS.gnssApi.baseUrl
    }${API_ENDPOINTS.gnssApi.endpoints.realtime_reports.getAlertAnalytics(
      groupName,
      stationId,
      alertId
    )}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
        "X-Tenant-Id": tenantId,
      },
    }
  );
  return response;
};

export const getAllAnalyticsByStationId = async (
  groupName,
  stationId,
  accessToken,
  tenantId
) => {

  const response = await fetch(
    `${
      API_ENDPOINTS.gnssApi.baseUrl
    }${API_ENDPOINTS.gnssApi.endpoints.realtime_reports.getAllAnalytics(
      groupName,
      stationId,
    )}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
        "X-Tenant-Id": tenantId,
      },
    }
  );
  return response;
};

export const getAnalyticsByStationIdAndDateInterval = async (
  groupName,
  stationId,
  startDate,
  stopDate,
  accessToken,
  tenantId
) => {

  const response = await fetch(
    `${
      API_ENDPOINTS.gnssApi.baseUrl
    }${API_ENDPOINTS.gnssApi.endpoints.realtime_reports.getAnalyticsByDateInterval(
      groupName,
      stationId,
      startDate,
      stopDate
    )}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
        "X-Tenant-Id": tenantId,
      },
    }
  );
  return response;
};
